import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Project } from '@/features/projects/types';
import { useCountries } from '@/hooks';
import { Checkbox, Field, Form, Input, Select, TextArea, Typography } from '@/components';
import { nullableStringSetter } from '@/helpers';

export type GeneralFormProps = {
  readonly id: string;
  readonly project: Project;
  readonly onSubmit: (data: any) => void;
};

export function GeneralFormNew({ id, project, onSubmit }: GeneralFormProps) {
  const { t } = useTranslation(['opportunities']);
  const { options: countriesOptions } = useCountries();

  const options = useMemo(
    () => ({
      defaultValues: {
        country: project.country,
        city: project.city,
        other_details: project.other_details ?? null,
      },
    }),
    [project]
  );

  const technologies = useMemo(() => {
    const collections = project.technologies?.map((tech) => tech.collection) ?? [];
    return {
      solar: collections.includes('projects_solar_technologies'),
      wind: collections.includes('projects_wind_technologies'),
      bess: collections.includes('projects_bess_technologies'),
      biogas: collections.includes('projects_biogas_technologies'),
      hydrogen: collections.includes('projects_hydrogen_technologies'),
      hidropower: collections.includes('projects_hidropower_technologies'),
      datacenter: collections.includes('projects_datacenter_technologies'),
    };
  }, [project.technologies]);

  return (
    <Form
      id={id}
      onSubmit={(data) => {
        onSubmit({ other_details: data.other_details });
      }}
      options={options}
    >
      {({ formState, register, watch }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 italic">
            {t('Please complete this section with general information about the project.')}
          </Typography>
          <div className="flex space-x-3">
            <Field
              id="country"
              label={t('Country')}
              error={formState.errors['country']}
              delay={0.6}
              classes={{ field: 'w-1/2' }}
            >
              <Select
                id="country"
                color={formState.errors['country'] ? 'error' : 'primary'}
                registration={register('country')}
                options={countriesOptions}
                className="w-full"
                readOnly
              />
            </Field>
            <Field
              id="city"
              label={t('City')}
              error={formState.errors['city']}
              delay={0.6}
              classes={{ field: 'w-1/2' }}
            >
              <Input
                id="city"
                color={formState.errors['city'] ? 'error' : 'primary'}
                autoComplete="off"
                registration={register('city')}
                readOnly
              />
            </Field>
          </div>
          <hr />
          <>
            <Typography as="p" color="primary" size="body-2" weight="semibold" className="mb-2">
              {t('Technology')}
            </Typography>

            <div className="flex flex-wrap items-center gap-6">
              {technologies.solar && (
                <Checkbox value="solar" checked={technologies.solar} disabled>
                  Solar PV
                </Checkbox>
              )}

              {technologies.wind && (
                <Checkbox value="wind" checked={technologies.wind} disabled>
                  Wind
                </Checkbox>
              )}

              {technologies.bess && (
                <Checkbox value="bess" checked={technologies.bess} disabled>
                  Bess
                </Checkbox>
              )}

              {technologies.biogas && (
                <Checkbox value="biogas" checked={technologies.biogas} disabled>
                  Biogas
                </Checkbox>
              )}

              {technologies.hydrogen && (
                <Checkbox value="hydrogen" checked={technologies.hydrogen} disabled>
                  Hydrogen
                </Checkbox>
              )}

              {technologies.hidropower && (
                <Checkbox value="hidropower" checked={technologies.hidropower} disabled>
                  Hydropower
                </Checkbox>
              )}

              {technologies.datacenter && (
                <Checkbox value="datacenter" checked={technologies.datacenter} disabled>
                  Data Center
                </Checkbox>
              )}
            </div>
          </>
          <hr />
          <Field
            id="other_details"
            label={t('Other details')}
            error={formState.errors['other_details']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="other_details"
              color="primary"
              registration={register('other_details', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
